import { FieldArray } from 'formik';
import { FormikProvider } from 'formik';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { queryCache } from 'react-query';
import { Spacer, TableSkeleton } from 'src/common';
import { Scope, SortState, VariableEdit } from './types';
import _ from 'lodash';
import {
  categorizeVariables,
  checkIsDirty,
  genEmptyVariable,
  sortVariables,
  transformVariablesToGqlInput,
  validationSchema,
} from './util';
import { useFormik } from 'formik';
import useScenarioNames from './useMappedNames';
import { motion } from 'framer-motion';
import { Head, Row, VariableActions } from './comps';
import Button from 'src/components/form/Button';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Div } from './styled';
import useAssessmentVariables from './useAssessmentVariables';
import { useAuth } from 'src/state/auth';
import { useCache } from 'src/graphql';
import { useUpsertAssessmentVariablesMutation, useUpsertWorkspaceVariablesMutation } from 'src/graphql/_generated';

interface WorkspaceVariablesFormProps {
  onClose: () => void;
  onCloseRequest?: () => void;
  scope: Scope;
  onDirtyChange?: (isDirty: boolean) => void;
  readOnly?: boolean;
  isModal?: boolean;
  hideEmptyVariables?: boolean;
  showAssessmentVarOnly?: boolean;
}

const WorkspaceVariablesForm: React.FC<WorkspaceVariablesFormProps> = ({
  onClose,
  onCloseRequest,
  scope,
  onDirtyChange,
  isModal,
  hideEmptyVariables = true,
  showAssessmentVarOnly,
}) => {
  const [initialVariables, setInitialVariables] = useState<VariableEdit[]>([]);
  const [error, setError] = useState('');
  const [variablesRepo, setVariablesRepo] = useState<VariableEdit[]>([]);

  const [sort, setSort] = useState<SortState>({ by: 'name', isAsc: true });
  const { variables: wVars, profile } = useAuth();
  const { assessmentVariables, isAssessmentVariablesLoading } = useAssessmentVariables({
    assessmentId: scope.type === 'assessment' ? scope.id : undefined,
  });

  const { scenarioNames, assessmentNames } = useScenarioNames({
    variables: variablesRepo,
  });
  const { refetchQuery } = useCache();

  const [upsertWorkspaceVariables] = useUpsertWorkspaceVariablesMutation();
  const [upsertAssessmentVariables] = useUpsertAssessmentVariablesMutation();

  useLayoutEffect(() => {
    if (!isAssessmentVariablesLoading) {
      const assessmentVariablesInit = assessmentVariables || [];
      const repo = [...wVars, ...assessmentVariablesInit];

      setVariablesRepo(repo);

      let vars: VariableEdit[];
      if (scope.type === 'workspace') {
        vars = wVars;
      } else {
        const wVarIds = new Set(wVars.map((wv) => wv.id));
        const cleanAssessmentVars = assessmentVariablesInit.filter((av) => !wVarIds.has(av.id));
        const uniqueAssessmentVars = Array.from(
          cleanAssessmentVars.reduce((map, v) => map.set(v.name, v), new Map<string, VariableEdit>()).values(),
        );
        const assessmentVarNames = uniqueAssessmentVars.map((v) => v.name);
        const wVarsWithoutOverrides = wVars.filter((wv) => !assessmentVarNames.includes(wv.name));
        vars = [...wVarsWithoutOverrides, ...uniqueAssessmentVars];
      }

      if (vars.length) {
        setInitialVariables(vars);
      } else {
        setInitialVariables([genEmptyVariable(scope.type)]);
      }
    }
  }, [isAssessmentVariablesLoading, scope, showAssessmentVarOnly, hideEmptyVariables, wVars, assessmentVariables]);

  const formik = useFormik({
    initialValues: {
      variables: initialVariables,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const variablesToSave = values.variables;
      const promises: Promise<unknown>[] = [];

      if (!showAssessmentVarOnly) {
        const isWorkspaceScope = scope.type === 'workspace';
        const excludeDeleteNames = isWorkspaceScope
          ? []
          : variablesToSave.filter((el) => !el.workspace_variable && !el.isGlobal).map((el) => el.name);

        const wvInput = isWorkspaceScope
          ? variablesToSave
          : variablesToSave.filter((el) => el.workspace_variable || el.isGlobal);
        const wvOriginal = isWorkspaceScope
          ? initialVariables
          : initialVariables.filter((el) => el.workspace_variable || el.isGlobal);
        const categorized = categorizeVariables(wvInput, wvOriginal, excludeDeleteNames, 'workspace');

        if (categorized.length) {
          promises.push(
            upsertWorkspaceVariables({
              variables: {
                workspaceId: profile?.activeWorkspace?.id!,
                variables: transformVariablesToGqlInput(categorized),
              },
            }),
          );
        }
      }

      if (scope.type === 'assessment') {
        const assessmentInput = variablesToSave.filter((el) => {
          if (el._isReverted) return false;
          if (el.workspace_variable || el.isGlobal) return false;

          const existsInAssessmentVars = assessmentVariables?.some((av) => av.id === el.id);
          const isNewOverride = !!el._isNewOverwrite;
          const isNewPureAssessmentVar = !!el._isNew && !wVars?.some((wv) => wv.name === el.name);
          return existsInAssessmentVars || isNewOverride || isNewPureAssessmentVar;
        });
        const assessmentOriginal = initialVariables.filter((el) => {
          if (el.workspace_variable || el.isGlobal) return false;
          return assessmentVariables?.some((av) => av.id === el.id) || el._isNewOverwrite || el._isNew;
        });
        const categorized = categorizeVariables(assessmentInput, assessmentOriginal, [], 'assessment');

        if (categorized.length) {
          promises.push(
            upsertAssessmentVariables({
              variables: {
                assessmentId: scope.id,
                variables: transformVariablesToGqlInput(categorized),
              },
            }),
          );
        }
      }

      return Promise.all(promises)
        .then(async () => {
          queryCache.invalidateQueries();
          await refetchQuery({ include: ['profile'] });
          onClose();
        })
        .catch((err: Error) => setError(err.message || 'Something went wrong'));
    },
  });

  const { values, handleSubmit, isValid, isSubmitting, setFieldValue, resetForm } = formik;

  const initialVariablesRef = useRef<string>('');
  useEffect(() => {
    const serialized = JSON.stringify(initialVariables.map((v) => ({ id: v.id, name: v.name })));
    if (initialVariablesRef.current && initialVariablesRef.current !== serialized) {
      resetForm({ values: { variables: initialVariables } });
    }
    initialVariablesRef.current = serialized;
  }, [initialVariables, resetForm]);

  useEffect(() => {
    const sorted = sortVariables(values.variables, sort);
    if (sorted) {
      setFieldValue('variables', sorted);
    }
  }, [sort]);

  const onOverride = (name: string, override: boolean) => {
    const original = wVars.find((el) => el.name === name);
    if (!override) {
      const newVariables = _.cloneDeep(values.variables);
      const idx = newVariables.findIndex((el) => el.name === name);

      if (original && idx !== -1) {
        newVariables[idx] = { ..._.cloneDeep(original), _isReverted: true };
        setFieldValue(`variables`, newVariables);
      }
    } else {
      const newVariables = _.cloneDeep(values.variables);
      const idx = newVariables.findIndex((el) => el.name === name);

      if (original && idx !== -1) {
        const targetVariable = _.cloneDeep(original);
        newVariables[idx] = {
          ...targetVariable,
          workspace_variable: false,
          isGlobal: false,
          isInUse: [],
          _isNewOverwrite: true,
        };

        setFieldValue(`variables`, newVariables);
      }
    }
  };

  const isFormDirty = checkIsDirty(values.variables, initialVariables);

  useEffect(() => {
    onDirtyChange?.(isFormDirty);
  }, [isFormDirty, onDirtyChange]);

  return (
    <Div>
      {!isAssessmentVariablesLoading ? (
        <FormikProvider value={formik}>
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.1 }}
            onSubmit={handleSubmit}
          >
            <div className="h-padding">
              <FieldArray name="variables">
                {({ remove, push }) => {
                  return (
                    <>
                      <div className="rows-grid">
                        <Head sort={sort} setSort={setSort} />
                        {values.variables.map((el, idx) => {
                          const hasWorkspaceVar = wVars?.some((wv) => wv.name === el.name);
                          const isVWar = el.workspace_variable || el.isGlobal || hasWorkspaceVar;
                          const isOverride =
                            scope.type === 'assessment' &&
                            hasWorkspaceVar &&
                            !el._isReverted &&
                            (assessmentVariables?.some((av) => av.name === el.name) || !!el._isNewOverwrite);
                          const canOverride = scope.type === 'assessment' && hasWorkspaceVar;
                          const shouldHideForAssessmentOnly = showAssessmentVarOnly && hasWorkspaceVar && !isOverride;
                          const isHidden = (hideEmptyVariables && el.value === null) || shouldHideForAssessmentOnly;
                          const typePrefix = el.workspace_variable ? 'w' : el.isGlobal ? 'g' : 'a';
                          const rowKey = `${typePrefix}-${el.id}`;

                          return (
                            <Row
                              key={rowKey}
                              idx={idx}
                              onRemove={() => {
                                if (isOverride && !el._isNew) {
                                  onOverride(el.name, false);
                                } else {
                                  remove(idx);
                                }
                              }}
                              scenarioNames={scenarioNames}
                              assessmentNames={assessmentNames}
                              scope={scope}
                              isVWar={isVWar}
                              canOverride={!!canOverride}
                              isOverride={!!isOverride}
                              onOverride={() => onOverride(el.name, !isOverride)}
                              isHidden={isHidden}
                              isGlobal={el.isGlobal}
                              existsInWorkspaceVars={!!hasWorkspaceVar}
                            />
                          );
                        })}
                      </div>
                      <Spacer $px={15} />
                      <VariableActions
                        originalVariables={scope.type === 'assessment' ? assessmentVariables || [] : wVars}
                        scope={scope}
                        onAddNewVariable={() => push(genEmptyVariable(scope.type))}
                        isModal={isModal}
                      />
                      <Spacer $px={35} />
                      <div className="btn-container">
                        {isModal && (
                          <Button secondary disabled={isSubmitting} onClick={() => (onCloseRequest || onClose)()}>
                            CANCEL
                          </Button>
                        )}
                        <Button
                          type="submit"
                          primary
                          disabled={!isValid || !isFormDirty || isSubmitting}
                          data-cy="save-variables-btn"
                          $constWidth={!isModal ? 220 : undefined}
                          onClick={() =>
                            mpEvent(MPEvents.ButtonClick, {
                              button: 'Save',
                              modal: isModal ? 'Variables modal' : undefined,
                              tags: ['VARIABLES'],
                            })
                          }
                        >
                          SAVE
                        </Button>
                      </div>
                    </>
                  );
                }}
              </FieldArray>
              {error && (
                <>
                  <Spacer $px={23} />
                  <div className="error">{error}</div>
                  <Spacer $px={23} />
                </>
              )}
            </div>
          </motion.form>
        </FormikProvider>
      ) : (
        <div className="h-padding">
          <TableSkeleton />
          <Spacer $px={54} />
        </div>
      )}
    </Div>
  );
};

export default WorkspaceVariablesForm;
